import React, { useEffect } from "react";
import Lights from "./Lights";
import Timer from "./Timer";
import LoadingChart from "./LoadingChart";
import NextLoad from "./NextLoad";
import OrderTable from "./OrderTable";
import PositionChange from "./PositionChange";
import "./DisplayIndex.scss";
import { useMeet } from "util/useMeet";
import { usePlatform } from "util/usePlatform";
import { useCurrentLifter } from "util/useCurrentLifter";
import { useCurrentAttempt } from "util/useCurrentAttempt";
import { useKnownLiftingOrder } from "util/useKnownLiftingOrder";
import { useCompleteLiftingOrder } from "util/useCompleteLiftingOrder";
import { useFedConfig } from "util/useFedConfig";
import LifterName from "./LifterName";
import AttemptInfo from "./AttemptInfo";
import TeamName from "./TeamName";
import DivisionInfo from "./DivisionInfo";
import RecordInfo from "./RecordInfo";
import classNames from "classnames";
import { DisplaySettingsProvider } from "./DisplaySettingsProvider";
import { SettingsDrawer } from "./SettingsDrawer";
import NextOverlay from "./NextOverlay";
import CurrentOverlay from "./CurrentOverlay";
import Marquee from "./Marquee";
import Image from "./Image";
import { getBarAndCollarsWeight } from "util/barLoad";
import Standings from "./Standings";

const DisplayIndex = () => {
  const meet = useMeet();
  const platform = usePlatform();
  const currentLifter = useCurrentLifter();
  const currentAttempt = useCurrentAttempt();
  const knownLiftingOrder = useKnownLiftingOrder();
  const completeLiftingOrder = useCompleteLiftingOrder();
  const federationConfig = useFedConfig();

  const [barChangeEvent, setBarChangeEvent] = React.useState(false);

  const barAndCollarsWeight = getBarAndCollarsWeight({
    attempt: currentAttempt,
    lifter: currentLifter,
    platform,
  });
  const barAndCollarsWeightRef = React.useRef(barAndCollarsWeight);
  const platformIdRef = React.useRef(platform._id);
  const barChangeEventTimer = React.useRef<NodeJS.Timeout | undefined>();
  useEffect(() => {
    if (
      platformIdRef.current === platform._id &&
      Number(barAndCollarsWeight) !== Number(barAndCollarsWeightRef.current)
    ) {
      setBarChangeEvent(true);

      barChangeEventTimer.current = setTimeout(() => {
        setBarChangeEvent(false);
      }, 5000);
    }

    barAndCollarsWeightRef.current = barAndCollarsWeight;

    return () => {
      if (barChangeEventTimer.current) {
        clearTimeout(barChangeEventTimer.current);
      }
    };
  }, [platform._id, barAndCollarsWeight]);

  useEffect(() => {
    if (meet.displayDefaultURL && !window.location.search) {
      window.location.search = meet.displayDefaultURL;
    }
  }, [meet.displayDefaultURL]);

  if (!platform || !meet) {
    return null;
  }

  return (
    <DisplaySettingsProvider>
      <div className={classNames("display-index2")}>
        <div className="display-settings-button">
          <SettingsDrawer />
        </div>
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <Image id="i" meet={meet} />
          <LifterName currentLifter={currentLifter} id="b" />
          <AttemptInfo meet={meet} currentAttempt={currentAttempt} id="a" />
          <TeamName meet={meet} currentLifter={currentLifter} id="t" />
          <DivisionInfo meet={meet} currentLifter={currentLifter} id="d" />
          <RecordInfo currentAttempt={currentAttempt} id="r" />
          <Timer platform={platform} id="time" />
          <PositionChange
            meet={meet}
            currentAttempt={currentAttempt}
            currentLifter={currentLifter}
            id="p"
          />
          <Lights meet={meet} platformId={platform._id} id="l" />
          <LoadingChart
            meet={meet}
            currentLifter={currentLifter}
            currentAttempt={currentAttempt}
            platform={platform}
            barChangeEvent={barChangeEvent}
            id="c"
          />
          <OrderTable
            meet={meet}
            platform={platform}
            completeLiftingOrder={completeLiftingOrder}
            id="o"
            federationConfig={federationConfig}
          />
          <NextLoad
            meet={meet}
            currentAttempt={currentAttempt}
            currentLifter={currentLifter}
            liftingOrder={knownLiftingOrder}
            platform={platform}
            id="n"
          />
          <NextOverlay id="no" platformId={platform._id} />
          <CurrentOverlay id="co" platformId={platform._id} />
          <Marquee
            meet={meet}
            currentLifter={currentLifter}
            currentAttempt={currentAttempt}
            id="m"
            platform={platform}
            barChangeEvent={barChangeEvent}
          />
          <Standings id="s" />
        </div>
      </div>
    </DisplaySettingsProvider>
  );
};

export default DisplayIndex;
